export type CronFrequency = 'minute' | 'hour' | 'day' | 'week' | 'month'

export type CronBuilderState = {
  frequency: CronFrequency
  minute: number
  hour: number
  daysOfWeek: number[]
  dayOfMonth: number
  minuteInterval: number
}

export const DAY_OF_WEEK_OPTIONS = [
  { value: 1, label: 'MON' },
  { value: 2, label: 'TUE' },
  { value: 3, label: 'WED' },
  { value: 4, label: 'THU' },
  { value: 5, label: 'FRI' },
  { value: 6, label: 'SAT' },
  { value: 0, label: 'SUN' },
] as const

export const FREQUENCY_OPTIONS = [
  { value: 'minute' as const, label: 'minute' },
  { value: 'hour' as const, label: 'hour' },
  { value: 'day' as const, label: 'day' },
  { value: 'week' as const, label: 'week' },
  { value: 'month' as const, label: 'month' },
]

export const DEFAULT_CRON_BUILDER_STATE: CronBuilderState = {
  frequency: 'day',
  minute: 0,
  hour: 9,
  daysOfWeek: [1],
  dayOfMonth: 1,
  minuteInterval: 15,
}

const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, index) => start + index)

export const MINUTE_OPTIONS = range(0, 59).map((value) => ({ value, label: String(value) }))
export const HOUR_OPTIONS = range(0, 23).map((value) => ({ value, label: String(value) }))
export const DAY_OF_MONTH_OPTIONS = range(1, 31).map((value) => ({ value, label: String(value) }))
export const MINUTE_INTERVAL_OPTIONS = [1, 5, 10, 15, 30].map((value) => ({ value, label: String(value) }))

function formatDaysOfWeek(days: number[]) {
  return [...days]
    .sort((a, b) => a - b)
    .map((day) => DAY_OF_WEEK_OPTIONS.find((option) => option.value === day)?.label)
    .filter(Boolean)
    .join(',')
}

function parseDayOfWeekField(field: string): number[] | null {
  if (field === '*') return null

  const days = new Set<number>()

  for (const part of field.split(',')) {
    if (part.includes('-')) {
      const [startText, endText] = part.split('-')
      const start = Number(startText)
      const end = Number(endText)
      if (!Number.isInteger(start) || !Number.isInteger(end) || start > end || start < 0 || end > 6) {
        return null
      }
      for (let day = start; day <= end; day += 1) {
        days.add(day)
      }
      continue
    }

    const day = Number(part)
    if (!Number.isInteger(day) || day < 0 || day > 6) {
      return null
    }
    days.add(day)
  }

  return [...days].sort((a, b) => a - b)
}

function parseNumericField(field: string): number | null {
  if (!/^\d+$/.test(field)) return null
  return Number(field)
}

function parseIntervalField(field: string): number | null {
  const match = field.match(/^\*\/(\d+)$/)
  if (!match) return null
  return Number(match[1])
}

export function buildCronExpression(state: CronBuilderState): string {
  const { frequency, minute, hour, daysOfWeek, dayOfMonth, minuteInterval } = state

  switch (frequency) {
    case 'minute':
      return minuteInterval > 1 ? `*/${minuteInterval} * * * *` : '* * * * *'
    case 'hour':
      return `${minute} * * * *`
    case 'day':
      return `${minute} ${hour} * * *`
    case 'week':
      return `${minute} ${hour} * * ${formatDaysOfWeek(daysOfWeek.length ? daysOfWeek : [1])}`
    case 'month':
      return `${minute} ${hour} ${dayOfMonth} * *`
    default:
      return `${minute} ${hour} * * *`
  }
}

export function parseCronExpression(expression: string): CronBuilderState | null {
  const parts = expression.trim().split(/\s+/)
  if (parts.length !== 5) return null

  const [minuteField, hourField, dayOfMonthField, monthField, dayOfWeekField] = parts
  if (monthField !== '*') return null

  const dayOfWeek = parseDayOfWeekField(dayOfWeekField)
  if (dayOfWeek && dayOfWeek.length > 0) {
    const minute = parseNumericField(minuteField)
    const hour = parseNumericField(hourField)
    if (minute === null || hour === null || dayOfMonthField !== '*') return null

    return {
      frequency: 'week',
      minute,
      hour,
      daysOfWeek: dayOfWeek,
      dayOfMonth: 1,
      minuteInterval: 15,
    }
  }

  const dayOfMonth = parseNumericField(dayOfMonthField)
  if (dayOfMonth !== null && dayOfWeekField === '*') {
    const minute = parseNumericField(minuteField)
    const hour = parseNumericField(hourField)
    if (minute === null || hour === null) return null

    return {
      frequency: 'month',
      minute,
      hour,
      daysOfWeek: [1],
      dayOfMonth,
      minuteInterval: 15,
    }
  }

  if (dayOfMonthField === '*' && dayOfWeekField === '*') {
    const hour = parseNumericField(hourField)
    const minute = parseNumericField(minuteField)

    if (hour !== null && minute !== null) {
      return {
        frequency: 'day',
        minute,
        hour,
        daysOfWeek: [1],
        dayOfMonth: 1,
        minuteInterval: 15,
      }
    }

    if (hourField === '*' && minute !== null) {
      return {
        frequency: 'hour',
        minute,
        hour: 0,
        daysOfWeek: [1],
        dayOfMonth: 1,
        minuteInterval: 15,
      }
    }

    const minuteInterval = parseIntervalField(minuteField)
    if (minuteInterval !== null && hourField === '*') {
      return {
        frequency: 'minute',
        minute: 0,
        hour: 0,
        daysOfWeek: [1],
        dayOfMonth: 1,
        minuteInterval,
      }
    }

    if (minuteField === '*' && hourField === '*') {
      return {
        frequency: 'minute',
        minute: 0,
        hour: 0,
        daysOfWeek: [1],
        dayOfMonth: 1,
        minuteInterval: 1,
      }
    }
  }

  return null
}

export function formatDaysOfWeekLabel(days: number[]) {
  return formatDaysOfWeek(days.length ? days : [1])
}
